/**
 * Which pages make up the document on screen, and what its files are called
 * when they leave the browser (#494).
 *
 * The viewer steps page by page, but a pharmacist prints and keeps a receita:
 * a three-page PDF is one document, not three.
 */

export interface DocumentPage {
  readonly url: string
  readonly attachmentId?: string | undefined
}

/**
 * Every page of the attachment `shown` belongs to, in the order listed.
 *
 * Pages with no attachment id count as one document: a golden-set case lists
 * its pages without one, and they are a single receita.
 */
export function pagesOfShownDocument<P extends DocumentPage>(
  pages: readonly P[],
  shown: P | undefined,
): readonly P[] {
  if (shown === undefined) return []
  return pages.filter((one) => one.attachmentId === shown.attachmentId)
}

const FALLBACK_BASE = 'receita'

/** `receita-<branch>-<quotation>`, the stem every downloaded page shares. */
export function prescriptionFileBase(of: {
  readonly branch_code: number | string
  readonly quotation_number: number | string
}): string {
  return `${FALLBACK_BASE}-${of.branch_code}-${of.quotation_number}`
}

/** What the browser can hand us as a page, by the extension people expect. */
const EXTENSION_BY_TYPE: Readonly<Record<string, string>> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
}

/**
 * `<base>-p<n>.<ext>`, with `n` counted from one within the document.
 *
 * An unknown type is named `.png`: the server renders every page to PNG, so
 * that is what a blob with no usable type almost certainly is.
 */
export function pageFileName(
  base: string | undefined,
  position: number,
  contentType: string,
): string {
  const type = contentType.split(';')[0]?.trim().toLowerCase() ?? ''
  const extension = EXTENSION_BY_TYPE[type] ?? 'png'
  return `${base ?? FALLBACK_BASE}-p${position + 1}.${extension}`
}
